import React, { useState, useEffect } from "react";
import {
  Form,
  Table,
  Tooltip,
  Button,
  Modal,
  InputNumber,
  Input,
  message,
  Space,
} from "antd";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit } from "@fortawesome/free-solid-svg-icons";

const DeliveryPricesInfo = ({ business, deliveryType }) => {
  const [pricingType, setPricingType] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [zones, setZones] = useState([]);
  const [editingZone, setEditingZone] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchZones();
  }, [business.id]);

  useEffect(() => {
    setPricingType(deliveryType);
    console.log("deliveryType", deliveryType);
  }, [deliveryType]);

  useEffect(() => {
    axiosWithAuth()
      .get(`/api/restaurant/shipping/deliveryGetSettings/${business.id}`)
      .then((response) => {
        console.log("response", response);
        setPricingType(response.data.type);
      })
      .catch(() => {
        message.error("Error loading delivery settings.");
      });
  }, [business.id]);

  const fetchZones = async () => {
    try {
      const response = await axiosWithAuth().get(
        `/api/restaurant/shipping/deliveryZonesGetAll/${business.id}`,
      );

      const formattedZones = response.data.map((zone) => ({
        key: zone.id,
        ...zone,
      }));

      setZones(formattedZones);
    } catch (error) {
      message.error("Failed to load delivery zones.");
    }
  };

  const handleAddOrUpdateZone = async (values) => {
    const postalCodesArray = values.postal_codes
      ? values.postal_codes.split(",").map((code) => code.trim())
      : [];
    const uniquePostalCodes = [...new Set(postalCodesArray)];

    if (
      pricingType === "fixed" &&
      !uniquePostalCodes.every((code) => /^\d{5}$/.test(code))
    ) {
      message.error("All postal codes must be 5 digits long and unique.");
      return;
    }

    // Validar solapamiento de rangos de distancia
    if (pricingType === "distance" && values.distance_range) {
      const rangeMatch = values.distance_range.match(
        /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/,
      );
      if (!rangeMatch) {
        message.error("Distance range must be in format: min-max (e.g., 0-15)");
        return;
      }

      const newMin = parseFloat(rangeMatch[1]);
      const newMax = parseFloat(rangeMatch[2]);

      if (newMin >= newMax) {
        message.error("Minimum distance must be less than maximum distance.");
        return;
      }

      // Verificar solapamiento con otras zonas existentes (excluyendo la que se está editando)
      const hasOverlap = zones.some((zone) => {
        if (editingZone && zone.id === editingZone.id) return false;
        if (!zone.distance_range) return false;

        const existingMatch = zone.distance_range.match(
          /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/,
        );
        if (!existingMatch) return false;

        const existingMin = parseFloat(existingMatch[1]);
        const existingMax = parseFloat(existingMatch[2]);

        // Verificar si hay solapamiento
        return newMin < existingMax && newMax > existingMin;
      });

      if (hasOverlap) {
        message.error(
          "Distance range overlaps with an existing zone. Please adjust the range.",
        );
        return;
      }
    }

    const zoneData = {
      restaurant_id: business.id,
      zone_name: values.zone_name,
      postal_codes: uniquePostalCodes.join(","),
      delivery_fee: values.delivery_fee,
      distance_range: pricingType === "distance" ? values.distance_range : null,
      type: pricingType,
    };

    try {
      if (editingZone) {
        // Actualizar zona existente
        await axiosWithAuth().put(
          "/api/restaurant/shipping/deliveryZoneUpdate",
          {
            id: editingZone.id,
            ...zoneData,
          },
        );
        message.success("Delivery zone updated successfully!");
      } else {
        // Crear nueva zona
        await axiosWithAuth().post(
          "/api/restaurant/shipping/deliveryZoneCreate",
          zoneData,
        );
        message.success("Delivery zone saved successfully!");
      }

      fetchZones();
      setModalVisible(false);
      setEditingZone(null);
      form.resetFields();
    } catch (error) {
      message.error("Failed to save delivery zone. Please try again.");
    }
  };

  const handleDelete = async (idZone) => {
    try {
      await axiosWithAuth().delete(
        `/api/restaurant/shipping/deliveryZone/${idZone}`,
      );
      setZones((prevZones) => prevZones.filter((zone) => zone.id !== idZone));
      message.success("Delivery zone deleted successfully!");
    } catch (error) {
      message.error("Failed to delete delivery zone. Please try again.");
    }
  };

  // Editar una zona
  const handleEdit = (record) => {
    setEditingZone(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  // Columnas de la tabla
  const columns = [
    { title: "Range Name", dataIndex: "zone_name", key: "zone_name" },
    ...(pricingType === "fixed"
      ? [
          {
            title: "Postal Codes",
            dataIndex: "postal_codes",
            key: "postal_codes",
          },
        ]
      : []),
    ...(pricingType === "distance"
      ? [
          {
            title: "Distance Range (miles)",
            dataIndex: "distance_range",
            key: "distance_range",
          },
        ]
      : []),
    {
      title: "Delivery Fee ($)",
      dataIndex: "delivery_fee",
      key: "delivery_fee",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <button onClick={() => handleEdit(record)}>
            <Tooltip title="Edit">
              <FontAwesomeIcon icon={faEdit} />
            </Tooltip>
          </button>
          <button onClick={() => handleDelete(record.id)}>
            <Tooltip title="Delete">
              <FontAwesomeIcon icon={faTrashAlt} />
            </Tooltip>
          </button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        onClick={() => {
          form.resetFields();
          setEditingZone(null);
          setModalVisible(true);
        }}
        disabled={!deliveryType}
      >
        Add Pricing Rule
      </Button>

      {deliveryType && (
        <>
          <Table
            columns={columns}
            dataSource={zones}
            style={{ marginTop: 16 }}
          />

          <Modal
            title={editingZone ? "Edit Pricing Rule" : "Add Pricing Rule"}
            open={modalVisible}
            onCancel={() => {
              setModalVisible(false);
              setEditingZone(null);
              form.resetFields();
            }}
            onOk={() => form.submit()}
          >
            <Form
              form={form}
              onFinish={handleAddOrUpdateZone}
              layout="vertical"
            >
              <Form.Item
                name="zone_name"
                label="Range Name"
                rules={[
                  { required: true, message: "Please enter a zone name" },
                ]}
              >
                <Input />
              </Form.Item>
              {pricingType === "fixed" && (
                <Form.Item
                  name="postal_codes"
                  label="Postal Codes"
                  rules={[
                    {
                      required: true,
                      message: "Enter at least one postal code",
                    },
                  ]}
                >
                  <Input placeholder="E.g.: 28001,28002" />
                </Form.Item>
              )}
              {pricingType === "distance" && (
                <Form.Item
                  name="distance_range"
                  label="Distance Range (miles)"
                  rules={[{ required: true, message: "Enter distance range" }]}
                >
                  <Input placeholder="E.g.: 0-15" />
                </Form.Item>
              )}
              <Form.Item
                name="delivery_fee"
                label="Delivery Fee ($)"
                rules={[{ required: true, message: "Enter a delivery fee" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}
    </div>
  );
};

export default DeliveryPricesInfo;
